import React from 'react';
import { CocoEvent } from '@cotera/client/app/etc';
import { BroadcastEventType, ResourceType } from '@cotera/api';
import { emit } from '@cotera/client/app/etc/broadcast-event-bus';
import { Assert } from '@cotera/utilities';
import { z } from 'zod/v3';
import type { QueryClient } from '@tanstack/react-query';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import {
  ResourceSelectionModel,
  ResourceType as SelectionResourceType,
} from '../selection/resource-selection.model';
import {
  RESOURCE_TYPE_CONFIG,
  type NonFolderResourceType,
} from '../type/resource-type-config';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { ConfirmDestructiveActionModal } from '@cotera/client/app/components/app';
import { ChatHistoryListPageModel } from '../../chat/models/chat-history-list-page.model';
import { AgentsListPageModel } from '../../agent/agents-list-page.model';
import { TriggersListModel } from '../../trigger/triggers-list.model';
import { DocumentsListPageModel } from '../../document/documents-list-page.model';
import { DatasetsListPageModel } from '../../dataset/datasets-list-page.model';
import { FolderContentsPageModel } from '../../folder/folder-contents-page.model';

const archiveResourceParamsSchema = z.object({
  // Optional per-invocation override for the confirm dialog subtitle — e.g. the
  // triggers list warns that archiving a trigger leaves its workflows unattached.
  confirmSubtitle: z.string().optional(),
});

type Params = z.infer<typeof archiveResourceParamsSchema>;

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

export type ArchiveResourceActionOptions = {
  /** When set, overrides the default confirmation subtitle (e.g. unsaved agent changes). */
  getAskSubtitle?: (context: ExecuteContext) => string | undefined;
  /** When set with {@link orgSlug}, navigates to the resource list after archiving a single item from a detail page. */
  navigate?: (path: string) => void;
  orgSlug?: string;
};

/** Detail pages (e.g. agent editor) can register a callback run immediately before navigating to the list after archive (e.g. skip navigation blocking). */
let archiveDetailPageBeforeNavigate: (() => void) | undefined;

export function registerArchiveDetailPageBeforeNavigate(
  fn: (() => void) | undefined
): void {
  archiveDetailPageBeforeNavigate = fn;
}

function isResourceListPageInScope(context: ApplicableContext): boolean {
  return (
    context.isInScope(ChatHistoryListPageModel) ||
    context.isInScope(AgentsListPageModel) ||
    context.isInScope(TriggersListModel) ||
    context.isInScope(DocumentsListPageModel) ||
    context.isInScope(DatasetsListPageModel) ||
    context.isInScope(FolderContentsPageModel)
  );
}

/**
 * Where to send a reader whose detail page has just been archived out from
 * under them.
 *
 * Not the type's own list route. `/datasets`, `/agents` and `/documents` were
 * folded into the Library when that became the one place resources are
 * browsed; they survive only for deep links and the type breadcrumb, and the
 * sidebar has no entry for them. Landing on one after an archive is how a
 * reader meets a page they had no other way to reach — the bug this answers.
 *
 * Chats and automations keep their own destination, because both are live
 * surfaces in the sidebar: a trigger goes to `/triggers`, which redirects on
 * to `/workflows` where triggers are listed.
 */
function archiveReturnSegment(type: NonFolderResourceType): string {
  switch (type) {
    case ResourceType.Chat:
    case ResourceType.Workflow:
    case ResourceType.WorkflowTrigger:
      return RESOURCE_TYPE_CONFIG[type].listRouteSegment;
    case ResourceType.Agent:
    case ResourceType.Document:
    case ResourceType.Dataset:
      return RESOURCE_TYPE_CONFIG[ResourceType.Folder].listRouteSegment;
    default:
      return Assert.unreachable(type);
  }
}

function shouldNavigateToListAfterArchive(
  context: ExecuteContext,
  targets: Array<{ type: NonFolderResourceType; id: string }>
): boolean {
  if (targets.length !== 1) {
    return false;
  }
  if (isResourceListPageInScope(context)) {
    return false;
  }
  return true;
}

/**
 * Generic archive action for any non-folder resource type.
 * Reads selections from ResourceSelectionModel and calls the generic resource.archive endpoint.
 * Add a new resource type to RESOURCE_TYPE_CONFIG and it is automatically supported here.
 */
export class ArchiveResourceAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Archive';
  /**
   * Acts on whatever resource is targeted, so it belongs to that resource's own
   * menu, not to a workspace-wide search. It stays registered and invokable —
   * `discrete` only keeps it out of the palette's list.
   *
   * Unlike {@link RenameChatAction}, there is no page-level fallback to stand in
   * for a selection: `getTargets` reads the {@link ResourceSelectionModel} and
   * nothing else. Offered in the palette it therefore acts on a selection the
   * palette has covered up, which is the one place the target cannot be seen.
   */
  discrete = true;

  description = 'Archive the selected resource(s)';
  inputSchema = archiveResourceParamsSchema;
  group = 'app';
  icon = 'archive-box';

  private _lastArchivedTargets: Array<{
    type: NonFolderResourceType;
    id: string;
  }> = [];

  /**
   * Targets snapshotted when the confirm dialog opens. `execute` runs on confirm
   * (an async gap after `ask`), and the shared {@link ResourceSelectionModel} can
   * be overwritten during that gap — e.g. the triggers accordion re-selects the
   * focused trigger on hover/focus when the row's kebab menu closes. Reading the
   * live selection at confirm time would then archive the wrong resource (the
   * whole trigger instead of the one workflow row). Snapshotting at ask time
   * pins the archive to what the user actually opened the dialog on.
   */
  private _pendingTargets:
    | Array<{ type: NonFolderResourceType; id: string }>
    | undefined;

  constructor(
    private readonly client: Pick<TenantedClient, 'resource'>,
    private readonly queryClient: QueryClient,
    private readonly orgId: string,
    private readonly modalManager: ModalManager,
    private readonly options?: ArchiveResourceActionOptions
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return this.getTargets(context).length > 0;
  }

  getPriority(context: ApplicableContext): number {
    const selection = context.targetedModelOfType(ResourceSelectionModel);
    if (selection !== null && this.getTargets(context).length > 0) {
      return 20;
    }
    return 0;
  }

  shouldAsk(_: ApplicableContext): boolean {
    return true;
  }

  ask(params: Params, context: ExecuteContext): AskResponse<Params> {
    const targets = this.getTargets(context);
    // Pin the archive to the current selection so a selection change while the
    // dialog is open can't retarget it (see `_pendingTargets`).
    this._pendingTargets = targets;
    const count = targets.length;
    const label = this.describeTargets(targets);
    // A per-call subtitle (e.g. the trigger "leaves workflows unattached"
    // warning) wins; otherwise fall back to the configured hook, then the
    // generic count-based prompt.
    const customSubtitle =
      params.confirmSubtitle ?? this.options?.getAskSubtitle?.(context);
    const subtitle =
      customSubtitle !== undefined && customSubtitle !== ''
        ? customSubtitle
        : count > 1
        ? `Are you sure you want to archive ${count} ${label}?`
        : `Are you sure you want to archive this ${label}?`;

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => (
            <ConfirmDestructiveActionModal
              title={`Archive ${label}`}
              subtitle={subtitle}
              confirmLabel="Archive"
              onConfirm={async () => {
                await onRespond({});
                this.modalManager.pop();
              }}
              onCancel={async () => {
                this._pendingTargets = undefined;
                await onCancel();
                this.modalManager.pop();
              }}
            />
          ),
          onClose: async () => {
            this._pendingTargets = undefined;
            await onCancel();
          },
        });
      },
    };
  }

  async execute(
    _params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    // Prefer the snapshot taken when the confirm dialog opened; fall back to the
    // live selection for direct (no-confirm) invocations.
    const targets = this._pendingTargets ?? this.getTargets(context);
    this._pendingTargets = undefined;
    this._lastArchivedTargets = targets;
    if (targets.length === 0) {
      return ok({});
    }

    const label = this.describeTargets(targets);
    const loadingToast = toast.loading(
      targets.length > 1
        ? `Archiving ${targets.length} ${label}...`
        : `Archiving ${label}...`,
      { id: 'archive-resource' }
    );

    try {
      for (const target of targets) {
        const result = await this.client.resource.archive({
          type: target.type,
          id: target.id,
        });
        if (result.isErr()) {
          const message =
            result.error.t === 'application'
              ? result.error.err.message
              : 'Failed to archive';
          loadingToast.error(message);
          return err([{ t: 'error', message }]);
        }
        await this.queryClient.removeQueries({
          queryKey: queryKeys.resource.get({
            orgId: this.orgId,
            type: target.type,
            id: target.id,
          }),
        });

        if (target.type === SelectionResourceType.Workflow) {
          await this.queryClient.removeQueries({
            queryKey: queryKeys.workflow.scheduledAgentBundle({
              orgId: this.orgId,
              workflowId: target.id,
            }),
          });
        }

        if (target.type === SelectionResourceType.Chat) {
          await this.queryClient.removeQueries({
            queryKey: queryKeys.chat.messages({
              orgId: this.orgId,
              conversationId: target.id,
            }),
          });
          await this.queryClient.removeQueries({
            queryKey: queryKeys.chat.status({
              orgId: this.orgId,
              conversationId: target.id,
            }),
          });
        }
      }

      loadingToast.success(
        targets.length > 1
          ? `${targets.length} ${label} archived`
          : `${label.charAt(0).toUpperCase()}${label.slice(1)} archived`
      );

      const affectedTypes = [...new Set(targets.map((t) => t.type))];
      for (const type of affectedTypes) {
        await this.queryClient.invalidateQueries({
          queryKey: queryKeys.resource.list({
            orgId: this.orgId,
            type,
            limit: 50,
          }),
        });
      }

      if (affectedTypes.includes(SelectionResourceType.Chat)) {
        await this.queryClient.invalidateQueries({
          queryKey: queryKeys.chat.conversations({ orgId: this.orgId }),
        });
      }

      if (affectedTypes.includes(SelectionResourceType.Agent)) {
        await this.queryClient.invalidateQueries({
          queryKey: queryKeys.agents.all({ orgId: this.orgId }),
        });
      }

      await this.queryClient.invalidateQueries({
        queryKey: [this.orgId, 'resource', 'folderContents'],
      });
      await this.queryClient.invalidateQueries({
        queryKey: [this.orgId, 'resource', 'unfiledTotals'],
      });
      await this.queryClient.invalidateQueries({
        queryKey: queryKeys.resource.folderTree({ orgId: this.orgId }),
      });

      const selection = context.targetedModelOfType(ResourceSelectionModel);
      if (selection !== null) {
        context.markAsNotTargeted(selection);
      }

      this.maybeNavigateToListAfterArchive(context, targets);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to archive';
      loadingToast.error(message);
      return err([{ t: 'error', message }]);
    }

    // Emit outside the try/catch: `emit` runs its `ResourceUpdated` listeners
    // synchronously, so a throw in any subscriber must not turn a completed
    // archive into a "Failed to archive" toast. Mirrors the rename actions,
    // which also emit outside their failure path. This restreams the resource
    // lists (Library, sidebar, type pages, folder contents) backed by
    // `ResourceUpdated` watchables so the archived item drops off without a
    // manual refresh, and drives `invalidateQueriesForResourceUpdated` for the
    // correct cache keys. Those watchables only recompute on this broadcast (or
    // on mount), which is why the hand-rolled invalidation above alone left the
    // stale row on screen.
    for (const target of targets) {
      emit(BroadcastEventType.ResourceUpdated, {
        orgId: this.orgId,
        resourceType: target.type,
        resourceId: target.id,
      });
    }

    return ok({});
  }

  private maybeNavigateToListAfterArchive(
    context: ExecuteContext,
    targets: Array<{ type: NonFolderResourceType; id: string }>
  ): void {
    const navigate = this.options?.navigate;
    const orgSlug = this.options?.orgSlug;
    if (navigate === undefined || orgSlug === undefined) {
      return;
    }
    if (!shouldNavigateToListAfterArchive(context, targets)) {
      return;
    }
    const first = targets[0];
    if (first === undefined) {
      return;
    }
    archiveDetailPageBeforeNavigate?.();
    navigate(`/${orgSlug}/${archiveReturnSegment(first.type)}`);
  }

  track(): ActionTrackMetadata | null {
    const targets = this._lastArchivedTargets;
    if (targets.length === 0) {
      return null;
    }
    const resourceTypes = [...new Set(targets.map((t) => t.type))];
    return {
      event: CocoEvent.ResourceArchived,
      properties: {
        resourceIds: targets.map((t) => t.id),
        resourceTypes,
        count: targets.length,
      },
    };
  }

  private getTargets(
    context: ApplicableContext
  ): Array<{ type: NonFolderResourceType; id: string }> {
    const selection = context.targetedModelOfType(ResourceSelectionModel);
    if (selection === null || selection.selections.length === 0) {
      return [];
    }
    return selection.selections
      .filter(
        (s): s is typeof s & { resourceType: NonFolderResourceType } =>
          s.resourceType !== SelectionResourceType.Folder &&
          s.resourceType in RESOURCE_TYPE_CONFIG
      )
      .map((s) => ({ type: s.resourceType, id: s.resourceId }));
  }

  /** Returns a human-readable label for the targets (e.g. "agent", "agents", "chat"). */
  private describeTargets(
    targets: Array<{ type: NonFolderResourceType; id: string }>
  ): string {
    if (targets.length === 0) {
      return 'resource';
    }
    const types = [...new Set(targets.map((t) => t.type))];
    if (types.length === 1 && types[0] !== undefined) {
      const config = RESOURCE_TYPE_CONFIG[types[0]];
      return targets.length === 1
        ? config.label.toLowerCase()
        : `${config.label.toLowerCase()}s`;
    }
    return targets.length === 1 ? 'resource' : 'resources';
  }
}
